import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut,
  Plus,
  Trash2,
  Users,
  CalendarDays,
  Inbox,
  X,
  Download,
  KeyRound,
  FileText,
} from "lucide-react";
import api, { API, formatApiError, clearAuthStorage } from "@/lib/api";
import ContentEditor from "@/pages/ContentEditor";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState("interests");
  const [showEvent, setShowEvent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const token = typeof window !== "undefined" ? window.localStorage.getItem("pl_token") : null;

  const meQuery = useQuery({
    queryKey: ["auth-me"],
    queryFn: () => api.get("/auth/me").then((r) => r.data),
    enabled: !!token,
    retry: false,
  });

  const interestsQuery = useQuery({
    queryKey: ["interests"],
    queryFn: () => api.get("/admin/interests").then((r) => r.data),
    enabled: !!meQuery.data,
  });
  const eventsQuery = useQuery({
    queryKey: ["events"],
    queryFn: () => api.get("/events").then((r) => r.data),
    enabled: !!meQuery.data,
  });
  const rsvpsQuery = useQuery({
    queryKey: ["rsvps"],
    queryFn: () => api.get("/admin/rsvps").then((r) => r.data),
    enabled: !!meQuery.data,
  });

  const deleteEventMut = useMutation({
    mutationFn: (id) => api.delete(`/admin/events/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["rsvps"] });
    },
  });

  const resetTerm4Mut = useMutation({
    mutationFn: () => api.post("/admin/events/reset-term4"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["rsvps"] });
      window.alert("Success — the 6 Term 4 2026 cohort workshops are now on your calendar.");
    },
    onError: (err) => {
      window.alert("Sorry, that didn't work: " + (formatApiError(err.response?.data?.detail) || err.message));
    },
  });

  const deleteInterestMut = useMutation({
    mutationFn: (id) => api.delete(`/admin/interests/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["interests"] }),
  });

  const deleteRsvpMut = useMutation({
    mutationFn: (id) => api.delete(`/admin/rsvps/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rsvps"] }),
  });

  if (!token) {
    navigate("/admin", { replace: true });
    return null;
  }
  if (meQuery.isError) {
    clearAuthStorage();
    navigate("/admin", { replace: true });
    return null;
  }
  if (meQuery.isLoading || !meQuery.data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--slate)]" data-testid="admin-loading">
        Loading admin...
      </div>
    );
  }

  const user = meQuery.data;
  const interests = interestsQuery.data || [];
  const events = eventsQuery.data || [];
  const rsvps = rsvpsQuery.data || [];

  const logout = () => {
    clearAuthStorage();
    navigate("/admin");
  };

  const deleteEvent = (id) => {
    if (!window.confirm("Delete this event and all its RSVPs?")) return;
    deleteEventMut.mutate(id);
  };

  const deleteInterest = (id, name) => {
    if (!window.confirm(`Delete the interest registration from "${name}"?`)) return;
    deleteInterestMut.mutate(id);
  };

  const deleteRsvp = (id, name) => {
    if (!window.confirm(`Delete the RSVP from "${name}"?`)) return;
    deleteRsvpMut.mutate(id);
  };

  const onEventCreated = () => {
    setShowEvent(false);
    qc.invalidateQueries({ queryKey: ["events"] });
  };

  const downloadCsv = async (path, filename) => {
    const t = window.localStorage.getItem("pl_token");
    try {
      const res = await fetch(`${API}${path}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Download failed: ${e.message}`);
    }
  };

  const counts = {
    interests: interests.length,
    events: events.length,
    rsvps: rsvps.length,
  };

  return (
    <div className="min-h-screen bg-[var(--bone)]" data-testid="admin-dashboard">
      <header className="bg-[var(--forest)] text-white">
        <div className="container-pl py-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[0.72rem] tracking-[0.22em] uppercase text-white/70 font-display font-bold">
              Admin
            </div>
            <div className="font-display font-semibold text-lg" data-testid="admin-user-name">
              {user?.name || user?.email}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setShowPassword(true)}
              className="text-white/85 hover:text-white text-sm inline-flex items-center gap-1.5"
              data-testid="admin-change-password-btn"
            >
              <KeyRound size={15} /> Change password
            </button>
            <Link to="/" className="text-white/85 hover:text-white text-sm" data-testid="admin-view-site">
              View site
            </Link>
            <button onClick={logout} className="btn btn-sand !py-2 !px-4" data-testid="admin-logout-btn">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-pl py-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={<Inbox size={20} />}
            label="Enrolments"
            value={counts.interests}
            active={tab === "interests"}
            onClick={() => setTab("interests")}
            testid="tab-interests"
          />
          <StatCard
            icon={<CalendarDays size={20} />}
            label="Scheduled events"
            value={counts.events}
            active={tab === "events"}
            onClick={() => setTab("events")}
            testid="tab-events"
          />
          <StatCard
            icon={<Users size={20} />}
            label="Total RSVPs"
            value={counts.rsvps}
            active={tab === "rsvps"}
            onClick={() => setTab("rsvps")}
            testid="tab-rsvps"
          />
          <StatCard
            icon={<FileText size={20} />}
            label="Edit website content"
            value={"—"}
            active={tab === "content"}
            onClick={() => setTab("content")}
            testid="tab-content"
          />
        </div>

        <div className="mt-8">
          {tab === "interests" && (
            <InterestsView
              rows={interests}
              onDelete={deleteInterest}
              onDownload={() =>
                downloadCsv(
                  "/admin/interests/export.csv",
                  `interest-registrations-${new Date().toISOString().slice(0, 10)}.csv`
                )
              }
            />
          )}
          {tab === "events" && (
            <EventsView
              events={events}
              rsvps={rsvps}
              onDelete={deleteEvent}
              onAdd={() => setShowEvent(true)}
              onResetTerm4={() => {
                if (
                  window.confirm(
                    "This will DELETE all existing events (and any RSVPs) and replace them with the 6 Term 4 2026 cohort workshops.\n\nContinue?"
                  )
                ) {
                  resetTerm4Mut.mutate();
                }
              }}
              resetting={resetTerm4Mut.isPending}
            />
          )}
          {tab === "rsvps" && (
            <RsvpsView
              rows={rsvps}
              events={events}
              onDelete={deleteRsvp}
              onDownload={() =>
                downloadCsv(
                  "/admin/rsvps/export.csv",
                  `rsvps-${new Date().toISOString().slice(0, 10)}.csv`
                )
              }
            />
          )}
          {tab === "content" && <ContentEditor />}
        </div>
      </div>

      {showEvent && <EventModal onClose={() => setShowEvent(false)} onCreated={onEventCreated} />}
      {showPassword && <ChangePasswordModal onClose={() => setShowPassword(false)} />}
    </div>
  );
}

function StatCard({ icon, label, value, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={`card text-left transition-all ${active ? "!border-[var(--forest)] shadow-lg" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-md bg-[var(--sage)] text-[var(--forest)] flex items-center justify-center">
          {icon}
        </div>
        <div className="text-[0.72rem] tracking-[0.22em] uppercase text-[var(--slate)] font-display font-bold">
          {label}
        </div>
      </div>
      <div className="font-display font-bold text-3xl mt-4">{value}</div>
    </button>
  );
}

function SectionHeader({ title, count, onDownload }) {
  return (
    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
      <h2 className="font-display font-semibold text-xl">
        {title} {count > 0 && <span className="text-[var(--slate)]">({count})</span>}
      </h2>
      {onDownload && count > 0 && (
        <button
          onClick={onDownload}
          className="btn btn-secondary !py-2 !px-4"
          data-testid="download-csv-btn"
        >
          <Download size={16} /> Download as spreadsheet
        </button>
      )}
    </div>
  );
}

function InterestsView({ rows, onDelete, onDownload }) {
  return (
    <div>
      <SectionHeader title="Enrolments" count={rows.length} onDownload={onDownload} />
      {rows.length === 0 ? (
        <EmptyState text="No enrolments yet. Each participant enrolled via the website will appear here as a separate row." />
      ) : (
        <div className="card !p-0 overflow-x-auto" data-testid="interests-table">
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="bg-[var(--sage)] text-[var(--slate)]">
              <tr>
                <Th>When</Th>
                <Th>Cohort</Th>
                <Th>Participant</Th>
                <Th>Participant email</Th>
                <Th>School</Th>
                <Th>Enrolled by</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[var(--border)]" data-testid={`interest-row-${r.id}`}>
                  <Td>{new Date(r.created_at).toLocaleDateString("en-NZ")}</Td>
                  <Td>{r.cohort || "—"}</Td>
                  <Td className="font-medium">{r.name}</Td>
                  <Td>
                    <a href={`mailto:${r.email}`} className="text-[var(--forest)]">
                      {r.email}
                    </a>
                  </Td>
                  <Td>{r.school}</Td>
                  <Td>
                    {r.enroller_name ? (
                      <div>
                        <div className="font-medium">{r.enroller_name}</div>
                        <div className="text-xs text-[var(--slate)]">
                          {r.enroller_role}
                          {r.enroller_email ? (
                            <>
                              {" · "}
                              <a href={`mailto:${r.enroller_email}`} className="text-[var(--forest)]">
                                {r.enroller_email}
                              </a>
                            </>
                          ) : null}
                        </div>
                      </div>
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td>
                    <button
                      onClick={() => onDelete(r.id, r.name)}
                      className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-xs"
                      data-testid={`delete-interest-${r.id}`}
                      title="Delete this registration"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EventsView({ events, rsvps, onDelete, onAdd, onResetTerm4, resetting }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-display font-semibold text-xl">
          Events {events.length > 0 && <span className="text-[var(--slate)]">({events.length})</span>}
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={onResetTerm4}
            disabled={resetting}
            className="btn btn-secondary !py-2 !px-4"
            data-testid="reset-term4-btn"
            title="Replace all events with the 6 Term 4 2026 cohort workshops"
          >
            <CalendarDays size={16} />
            {resetting ? "Resetting..." : "Reset to Term 4 schedule"}
          </button>
          <button onClick={onAdd} className="btn btn-primary !py-2 !px-4" data-testid="add-event-btn">
            <Plus size={16} /> New event
          </button>
        </div>
      </div>
      {events.length === 0 ? (
        <EmptyState text="No events yet. Add one to start gathering RSVPs." />
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="events-grid">
          {events.map((e) => {
            const count = rsvps.filter((r) => r.event_id === e.id).length;
            return (
              <div key={e.id} className="card" data-testid={`event-card-${e.id}`}>
                <div className="overline">
                  {e.date} · {e.time}
                </div>
                <h3 className="font-display font-semibold text-lg mt-1">{e.title}</h3>
                {e.location && <div className="text-sm text-[var(--slate)] mt-1">{e.location}</div>}
                {e.description && <p className="mt-3 text-sm text-[var(--slate)]">{e.description}</p>}
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm">
                    <strong>{count}</strong> RSVP{count === 1 ? "" : "s"}
                    {e.capacity ? ` / ${e.capacity}` : ""}
                  </span>
                  <button
                    onClick={() => onDelete(e.id)}
                    className="text-red-600 hover:text-red-700 flex items-center gap-1 text-sm"
                    data-testid={`delete-event-${e.id}`}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RsvpsView({ rows, events, onDelete, onDownload }) {
  const eventMap = Object.fromEntries(events.map((e) => [e.id, e]));
  return (
    <div>
      <SectionHeader title="Event RSVPs" count={rows.length} onDownload={onDownload} />
      {rows.length === 0 ? (
        <EmptyState text="No RSVPs yet. When someone reserves a place on a calendar event, they'll appear here." />
      ) : (
        <div className="card !p-0 overflow-x-auto" data-testid="rsvps-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-[var(--sage)] text-[var(--slate)]">
              <tr>
                <Th>When</Th>
                <Th>Event</Th>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>School</Th>
                <Th>Role</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[var(--border)]" data-testid={`rsvp-row-${r.id}`}>
                  <Td>{new Date(r.created_at).toLocaleDateString("en-NZ")}</Td>
                  <Td>{eventMap[r.event_id]?.title || "—"}</Td>
                  <Td className="font-medium">{r.name}</Td>
                  <Td>
                    <a href={`mailto:${r.email}`} className="text-[var(--forest)]">
                      {r.email}
                    </a>
                  </Td>
                  <Td>{r.school || "—"}</Td>
                  <Td>{r.role || "—"}</Td>
                  <Td>
                    <button
                      onClick={() => onDelete(r.id, r.name)}
                      className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-xs"
                      data-testid={`delete-rsvp-${r.id}`}
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EventModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    date: "",
    time: "",
    location: "",
    capacity: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const payload = { ...form, capacity: form.capacity ? Number(form.capacity) : null };
      await api.post("/admin/events", payload);
      await onCreated();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="event-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 md:p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-md hover:bg-[var(--sage)]"
          data-testid="event-modal-close"
        >
          <X size={20} />
        </button>
        <h2 className="font-display font-bold text-2xl">New event</h2>
        <form onSubmit={submit} className="mt-5 space-y-4" data-testid="event-form">
          <div className="field">
            <label htmlFor="ev-title">Title</label>
            <input
              id="ev-title"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="event-title-input"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="field">
              <label htmlFor="ev-date">Date</label>
              <input
                id="ev-date"
                type="date"
                required
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="event-date-input"
              />
            </div>
            <div className="field">
              <label htmlFor="ev-time">Time</label>
              <input
                id="ev-time"
                placeholder="10:00 AM – 2:00 PM"
                required
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                data-testid="event-time-input"
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="field">
              <label htmlFor="ev-location">Location</label>
              <input
                id="ev-location"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                data-testid="event-location-input"
              />
            </div>
            <div className="field">
              <label htmlFor="ev-capacity">Capacity</label>
              <input
                id="ev-capacity"
                type="number"
                min="1"
                value={form.capacity}
                onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                data-testid="event-capacity-input"
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="ev-desc">Description</label>
            <textarea
              id="ev-desc"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="event-description-input"
            />
          </div>
          {err && (
            <div className="text-sm text-red-700 bg-red-50 rounded-md px-3 py-2" data-testid="event-form-error">
              {err}
            </div>
          )}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1" data-testid="event-cancel-btn">
              Cancel
            </button>
            <button type="submit" disabled={busy} className="btn btn-primary flex-1" data-testid="event-submit-btn">
              {busy ? "Creating..." : "Create event"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setOk(false);
    if (next.length < 8) {
      setErr("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setErr("New password and confirmation do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setOk(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="password-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 md:p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-md hover:bg-[var(--sage)]"
          data-testid="password-modal-close"
        >
          <X size={20} />
        </button>
        <div className="w-11 h-11 rounded-lg bg-[var(--forest)] text-white flex items-center justify-center">
          <KeyRound size={18} />
        </div>
        <h2 className="font-display font-bold text-2xl mt-4">Change your password</h2>
        <p className="text-sm text-[var(--slate)] mt-1">Pick something only you know. Minimum 8 characters.</p>

        <form onSubmit={submit} className="mt-5 space-y-4" data-testid="password-form">
          <label className="inline-flex items-center gap-2 text-sm text-[var(--slate)] cursor-pointer" data-testid="password-show-toggle">
            <input
              type="checkbox"
              checked={showPw}
              onChange={(e) => setShowPw(e.target.checked)}
              className="w-4 h-4 accent-[var(--forest)]"
            />
            Show passwords (so you can see exactly what you're typing)
          </label>
          <div className="field">
            <label htmlFor="pw-current">Current password</label>
            <input
              id="pw-current"
              type={showPw ? "text" : "password"}
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="password-current-input"
            />
          </div>
          <div className="field">
            <label htmlFor="pw-new">New password</label>
            <input
              id="pw-new"
              type={showPw ? "text" : "password"}
              autoComplete="new-password"
              required
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="password-new-input"
            />
            <div className="text-xs text-[var(--slate)] mt-1">
              At least 8 characters. Write it down somewhere safe before saving.
            </div>
          </div>
          <div className="field">
            <label htmlFor="pw-confirm">Confirm new password</label>
            <input
              id="pw-confirm"
              type={showPw ? "text" : "password"}
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="password-confirm-input"
            />
            {next && confirm && next !== confirm && (
              <div className="text-xs text-red-700 mt-1">Passwords don&apos;t match yet</div>
            )}
            {next && confirm && next === confirm && (
              <div className="text-xs text-[var(--forest)] mt-1">✓ Passwords match</div>
            )}
          </div>

          {err && (
            <div className="text-sm text-red-700 bg-red-50 rounded-md px-3 py-2" data-testid="password-error">
              {err}
            </div>
          )}
          {ok && (
            <div
              className="text-sm text-[var(--forest)] bg-[var(--sage)] rounded-md px-3 py-2"
              data-testid="password-success"
            >
              Password updated! Please use your new password next time you sign in.
            </div>
          )}

          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
              Close
            </button>
            <button type="submit" disabled={busy} className="btn btn-primary flex-1" data-testid="password-submit-btn">
              {busy ? "Saving..." : "Update password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="text-left font-display font-semibold uppercase text-[0.7rem] tracking-[0.2em] px-4 py-3">
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}
function EmptyState({ text }) {
  return (
    <div className="card text-center text-[var(--slate)]" data-testid="empty-state">
      {text}
    </div>
  );
}
